import React, { useCallback, useMemo, useState, useEffect, useRef } from "react"
import { LngLatBounds } from "mapbox-gl"
import Map from "./Map"
import PlaceCards from "./PlaceCards"
import MapCards from "./MapCards"
import LayoutControls from "./LayoutControls"
import MapLogo from "./MapLogo"
import Filters from "./Filters"
import PlaceDetails from "./PlaceDetails"
import type { MapLayoutProps, Place } from "../../types"
import "./styles/MapLayout.scss"
import { Grid } from "@mui/material"
import type { ViewMode } from "../../types"

const MODES: { LIST: ViewMode; MAP: ViewMode } = {
  LIST: "list",
  MAP: "map",
} as const


const MapLayout: React.FC<MapLayoutProps> = ({
  places,
  placeCurrent,
  mode,
  vibes,
  categories,
  showFilters,
  themeObject,
  showForPrint,
  showMapCards,
  cardStyle,
  handleModeToggle,
  handleCardClick,
  handleCardFocus,
  handleMarkerClick,
  mapConfig,
  mapRef,
  showLogo,
  showLeft = true,
  mapHeight = "100%",
  mapWidth = "100%",
  showBoundary,
  boundaryCoordinates,
  showMarkers = true,
  useMobileView = false,
  showCards = true,
  onChange,
  selectedVibes = [],
  selectedCategories = [],
  showHeat = false,
}) => {
  const isListMode = mode === MODES.LIST
  const isMapMode = mode === MODES.MAP
  const isMobileList = useMobileView && isListMode

  const [searchTerm, setSearchTerm] = useState("")
  const swiperRef = useRef<{ slideTo: (index: number) => void } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(
    useMobileView ? 0 : window.innerWidth
  )

  useEffect(() => {
    if (!containerRef.current) return
    
    const updateWidth = (width: number) => {
      setContainerWidth(width)
      if (mapRef?.current) {
        setTimeout(() => {
          mapRef.current?.resize()
        }, 0)
      }
    }

    updateWidth(containerRef.current.offsetWidth)

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateWidth(entry.contentRect.width)
      }
    })

    resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [mapRef])

  const isContainerMobile = containerWidth < 1024
  const shouldUseMobileLayout = useMobileView || isContainerMobile

  const placesInBounds = useMemo(
    () =>
      places.filter((place) => {
        if (!boundaryCoordinates?.length) return true

        const bounds = new LngLatBounds()
        boundaryCoordinates.forEach((coord) => bounds.extend(coord))

        return bounds.contains(place.geometry.coordinates)
      }),
    [places, boundaryCoordinates]
  )

  const filteredPlaces = useMemo(
    () =>
      placesInBounds.filter((place) => {
        if (searchTerm) {
          const searchFields = [
            place.properties.name,
            place.properties.description,
            place.properties.address,
            ...(place.properties.categories?.map((c) => c.name) || []),
            ...(place.properties.vibes?.map((v) => v.name) || []),
            place.properties.neighborhood,
            place.properties.city,
          ]
            .filter(Boolean)
            .map((field) => (field as string).toLowerCase())

          const termMatches = searchTerm
            .toLowerCase()
            .split(" ")
            .every((term) => searchFields.some((field) => field.includes(term)))

          if (!termMatches) return false
        }

        const hasSelectedVibe =
          !selectedVibes?.length || selectedVibes.some((vibe) => place.properties.vibes?.some((placeVibe) => placeVibe.id.toString() === vibe.id.toString()))

        const hasSelectedCategory =
          !selectedCategories?.length ||
          selectedCategories.some((category) => place.properties.categories?.some((placeCategory) => placeCategory.id.toString() === category.id.toString()))

        return hasSelectedVibe && hasSelectedCategory
      }),
    [placesInBounds, selectedVibes, selectedCategories, searchTerm]
  )

  const sortedPlaces = useMemo(
    () => [...filteredPlaces].sort((a, b) => (b.properties.score_combined || 0) - (a.properties.score_combined || 0)),
    [filteredPlaces]
  )

  const availableTaxonomies = useMemo(() => {
    const usedVibeIds = new Set<string>()
    const usedCategoryIds = new Set<string>()

    placesInBounds.forEach((place) => {
      place.properties.vibes?.forEach((vibe) => {
        usedVibeIds.add(vibe.id.toString())
      })
      place.properties.categories?.forEach((category) => {
        usedCategoryIds.add(category.id.toString())
      })
    })

    return {
      vibes: vibes.filter((vibe) => usedVibeIds.has(vibe.id.toString())),
      categories: categories.filter((category) => usedCategoryIds.has(category.id.toString())),
    }
  }, [placesInBounds, vibes, categories])

  const handleFilterChange = useCallback(
    (filterUpdate) => {
      if ("searchTerm" in filterUpdate) {
        setSearchTerm(filterUpdate.searchTerm)
      }
      onChange?.(filterUpdate)
    },
    [onChange]
  )

  const heatmapData = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: places,
    }),
    [places]
  )

  const handleMapCardClick = (place: Place) => {
    handleMarkerClick(place)
  }

  const handleMapCardFocus = (place: Place) => {
    handleCardFocus(place)
  }

  const handleMapMarkerClick = (place: Place | null) => {
    const showAsMobile = useMobileView

    if (showAsMobile) {
      if (place) {
        handleCardFocus(place)
        const cardIndex = sortedPlaces.findIndex((p) => p.id === place.id)
        if (cardIndex >= 0 && swiperRef.current) {
          swiperRef.current.slideTo(cardIndex)
        }
      }
    } else {
      handleMarkerClick(place)
    }
  }

  return (
    <div ref={containerRef} className={`map-layout ${showForPrint ? "print" : ""} ${isListMode ? "list-mode" : "map-mode"}`} style={{ height: mapHeight }}>
      <Grid container>
        {showLeft && !shouldUseMobileLayout && (
          <Grid item xs={12} md={5} xl={4} sx={{ boxShadow: 1 }} zIndex={20}>
            <div className="leftPanel" style={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>
              {placeCurrent && (
                <div className={`place-details-overlay ${useMobileView ? "mobile" : ""}`}>
                  <PlaceDetails place={placeCurrent} showMobileView={useMobileView} onClose={() => handleMarkerClick(null)} />
                </div>
              )}

              {!placeCurrent && (
                <>
                  {showFilters && (
                    <Filters
                      vibes={availableTaxonomies.vibes}
                      categories={availableTaxonomies.categories}
                      selectedVibes={selectedVibes}
                      selectedCategories={selectedCategories}
                      onChange={handleFilterChange}
                    />
                  )}
                  {showCards && (
                    <div className="cardWrapper" style={{ flexGrow: 1, overflow: "auto", minHeight: 0, width: "100%" }}>
                      <PlaceCards places={sortedPlaces} onClick={handleCardClick} onFocus={handleCardFocus} cardStyle={cardStyle} />
                    </div>
                  )}
                </>
              )}
            </div>
          </Grid>
        )}

        <Grid item xs={12} sm={12} md={shouldUseMobileLayout ? 12 : 7} xl={shouldUseMobileLayout ? 12 : 8} style={{ height: "100%" }}>
          <div className="map-wrapper" style={{ height: "100%", position: "relative" }}>
            {showFilters && (shouldUseMobileLayout || useMobileView) && (
              <Filters
                vibes={availableTaxonomies.vibes}
                categories={availableTaxonomies.categories}
                selectedVibes={selectedVibes}
                selectedCategories={selectedCategories}
                onChange={handleFilterChange}
              />
            )}

            {showLogo && !isMobileList && <MapLogo />}

            <Map
              mapRef={mapRef}
              {...mapConfig}
              places={filteredPlaces}
              selectedPlace={placeCurrent}
              onPlaceSelect={handleMapMarkerClick}
              showMarkers={showMarkers}
              showBoundary={showBoundary}
              boundaryCoordinates={boundaryCoordinates}
              height={mapHeight}
              width={mapWidth}
              showHeat={showHeat}
              heatmapData={heatmapData}
              useMobileView={useMobileView}
            />

            {shouldUseMobileLayout && isMapMode && showMapCards && filteredPlaces.length > 0 && (
              <MapCards places={sortedPlaces} onClick={handleMapCardClick} onFocus={handleMapCardFocus} ref={swiperRef} />
            )}

            {useMobileView && placeCurrent && (
              <div className="place-details-overlay mobile">
                <PlaceDetails place={placeCurrent} showMobileView={true} onClose={() => handleMarkerClick(null)} />
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

export default MapLayout
